import { Link } from "react-router-dom";
import { MessageSquare, Upload, Search, ShieldCheck, ClipboardCheck, Compass, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";

const STEPS = [
  { icon: <ClipboardCheck />, t: "Join — Annual Health Check included", d: "Your membership includes an Annual External Park Home Health Check — an external visual condition review of your home with maintenance guidance." },
  { icon: <MessageSquare />, t: "Tell us when something happens", d: "Submit a quick form telling us about the issue, the home and the urgency. Members get priority." },
  { icon: <Upload />,        t: "Upload photos, documents, reports or quotes", d: "Drag in supporting files — we accept images, videos, PDFs and Word docs." },
  { icon: <Search />,        t: "We review the issue", d: "A specialist reviews everything you've sent and identifies what is really going on." },
  { icon: <ShieldCheck />,   t: "We identify risks, cost concerns or insurance considerations", d: "If the quote is excessive, the scope is wrong, or insurance may apply — we tell you." },
  { icon: <Compass />,       t: "We help you move forward properly", d: "Clear next steps, contractor guidance, and ongoing member support all year round." },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <SEO
        title="How Park Home Support Works | Annual Health Check + Specialist Reviews"
        description="An annual support membership for park home owners. We carry out a yearly external Health Check, review your contractor quotes, and provide technical support whenever something goes wrong."
        keywords="how park home support works, park home health check, park home advice process, park home expert review UK"
        canonical="https://parkhomesupport.co.uk/how-it-works"
      />
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <span className="eyebrow">How It Works</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl text-[#071A33] leading-[1.05]">
            Simple. Practical. <span className="text-[#064E2E]">Built Around Real Park Home Problems.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#334155]">
            We make it simple to get specialist input on your park home — without pressure, contractor bias or hidden agendas.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs">
          <ol className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {STEPS.map((s, i) => (
              <li key={s.t} className="card-default flex gap-5" data-testid={`step-${i+1}`}>
                <div className="shrink-0">
                  <div className="w-12 h-12 bg-[#064E2E] text-white rounded-xl flex items-center justify-center">{s.icon}</div>
                  <p className="mt-3 font-display text-3xl text-[#B88A2A] text-center">{String(i+1).padStart(2,"0")}</p>
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#071A33]">{s.t}</h3>
                  <p className="mt-2 text-[#334155] leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-16 card-dark text-center">
            <h3 className="font-display text-3xl">Ready to start?</h3>
            <p className="mt-3 text-white/80 max-w-xl mx-auto">Submit a request now or join the support plan to lock in expert help year-round.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/request-support" className="btn-accent" data-testid="hiw-request-btn">Request Support <ArrowRight size={16}/></Link>
              <Link to="/membership" className="btn-outline border-white text-white hover:bg-white hover:text-[#071A33]" data-testid="hiw-join-btn">Join Now</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
